import React from 'react';
import { AlertTriangle, RefreshCw, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export const glassPanel =
  'glass rounded-[1.75rem] border border-black/10 bg-white/65 text-card-foreground shadow-[0_8px_30px_-12px_rgba(0,0,0,0.18)] backdrop-blur-2xl backdrop-saturate-150 dark:border-white/10 dark:bg-white/[0.06] dark:shadow-[0_16px_40px_-20px_rgba(0,0,0,0.7)] sm:rounded-[1.75rem]';

export const glassStrong =
  'glass-strong rounded-[1.75rem] border border-black/10 bg-white/80 text-card-foreground shadow-[0_12px_40px_-16px_rgba(0,0,0,0.22)] backdrop-blur-2xl backdrop-saturate-150 dark:border-white/15 dark:bg-white/10 dark:shadow-[0_20px_50px_-24px_rgba(0,0,0,0.8)] sm:rounded-[1.75rem]';

export const glassSubtle =
  'glass-subtle rounded-2xl border border-black/5 bg-white/50 text-card-foreground shadow-sm backdrop-blur-xl backdrop-saturate-150 dark:border-white/10 dark:bg-white/[0.045]';

const iconTone = {
  neutral: 'bg-foreground/5 text-foreground',
  success: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  info: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
  warning: 'bg-amber-500/15 text-amber-800 dark:text-amber-200',
  danger: 'bg-red-500/15 text-red-700 dark:text-red-300',
  accent: 'bg-primary/10 text-primary',
};

const pillTone = {
  neutral: 'bg-foreground/5 text-muted-foreground ring-foreground/10',
  success: 'bg-emerald-500/15 text-emerald-800 ring-emerald-500/25 dark:text-emerald-200',
  info: 'bg-sky-500/15 text-sky-800 ring-sky-500/25 dark:text-sky-200',
  warning: 'bg-amber-500/15 text-amber-900 ring-amber-500/30 dark:text-amber-100',
  danger: 'bg-red-500/15 text-red-800 ring-red-500/25 dark:text-red-200',
};

export const REQUEST_STATUSES = [
  { value: 'draft', label: 'Draft', tone: 'neutral' },
  { value: 'pending_approval', label: 'Pending approval', tone: 'warning' },
  { value: 'approved_ready_installation', label: 'Ready to install', tone: 'info' },
  { value: 'needs_modification', label: 'Needs changes', tone: 'warning' },
  { value: 'rejected', label: 'Rejected', tone: 'danger' },
  { value: 'installation_in_progress', label: 'Installing', tone: 'info' },
  { value: 'installed', label: 'Installed', tone: 'success' },
];

export const chartTooltipStyle = {
  background: 'hsl(var(--popover) / 0.92)',
  border: '1px solid hsl(var(--border))',
  borderRadius: 16,
  color: 'hsl(var(--popover-foreground))',
  boxShadow: '0 12px 40px hsl(var(--foreground) / 0.12)',
};

export const chartAxisProps = {
  tick: { fill: 'hsl(var(--muted-foreground))', fontSize: 12 },
  axisLine: false,
  tickLine: false,
};

export function errorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const text = detail.map((item) => item?.msg || item).filter(Boolean).join(', ');
    if (text) return text;
  }
  return fallback;
}

export function humanize(value) {
  const text = String(value ?? '')
    .replace(/[._]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!text) return '-';
  return text.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function Page({ children, testId, className }) {
  return (
    <div className={cn('relative isolate space-y-6 sm:space-y-8', className)} data-testid={testId}>
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -left-16 -top-24 h-64 w-64 rounded-full bg-emerald-400/30 blur-3xl dark:bg-emerald-400/15" />
        <div className="absolute -right-12 top-20 h-72 w-72 rounded-full bg-sky-400/25 blur-3xl dark:bg-sky-400/10" />
        <div className="absolute left-1/3 top-1/2 h-64 w-64 rounded-full bg-amber-300/20 blur-3xl dark:bg-amber-300/10" />
        <div className="absolute bottom-8 right-8 h-56 w-56 rounded-full bg-violet-400/20 blur-3xl dark:bg-violet-400/10" />
      </div>
      {children}
    </div>
  );
}

export function GlassCard({ tone = 'default', className, children, ...props }) {
  const surface = tone === 'strong' ? glassStrong : tone === 'subtle' ? glassSubtle : glassPanel;
  return (
    <div className={cn('relative', surface, className)} {...props}>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-white/80 to-transparent dark:via-white/25"
      />
      {children}
    </div>
  );
}

export function PageHeader({ title, description, actions }) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0 space-y-1">
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">{title}</h1>
        {description ? <p className="max-w-2xl text-sm text-muted-foreground sm:text-base">{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function SectionHeading({ icon: Icon, title, description, action }) {
  return (
    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <h2 className="flex items-center gap-2 text-base font-semibold tracking-tight">
          {Icon ? <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" /> : null}
          {title}
        </h2>
        {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function StatCard({ label, value, hint, icon: Icon, tone = 'neutral', onClick, testId }) {
  const Comp = onClick ? 'button' : 'div';
  return (
    <Comp
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      data-testid={testId}
      className={cn(
        glassSubtle,
        'w-full p-4 text-left sm:p-5',
        onClick && 'transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="mt-1 break-words text-xl font-semibold leading-tight tracking-tight tabular-nums sm:text-2xl">{value}</p>
          {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
        </div>
        {Icon ? (
          <div className={cn('flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl', iconTone[tone] || iconTone.neutral)}>
            <Icon className="h-5 w-5" aria-hidden="true" />
          </div>
        ) : null}
      </div>
    </Comp>
  );
}

export function EmptyState({ icon: Icon, title, description, action, compact = false }) {
  return (
    <div className={cn('flex flex-col items-center justify-center px-4 text-center', compact ? 'py-8' : 'py-14')}>
      {Icon ? (
        <div className={cn('mb-3 flex items-center justify-center rounded-2xl bg-foreground/5 text-muted-foreground', compact ? 'h-11 w-11' : 'mb-4 h-14 w-14')}>
          <Icon className={compact ? 'h-5 w-5' : 'h-7 w-7'} aria-hidden="true" />
        </div>
      ) : null}
      <p className="font-medium">{title}</p>
      {description ? <p className="mt-1 max-w-sm text-sm text-muted-foreground">{description}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function ErrorState({ title = 'Could not load this page', message, onRetry, retryLabel = 'Try again' }) {
  return (
    <GlassCard className="flex flex-col items-start gap-4 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6" role="alert">
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
          <AlertTriangle className="h-5 w-5" aria-hidden="true" />
        </div>
        <div>
          <p className="font-medium">{title}</p>
          {message ? <p className="mt-1 text-sm text-muted-foreground">{message}</p> : null}
        </div>
      </div>
      {onRetry ? (
        <Button type="button" variant="outline" className="rounded-full" onClick={onRetry}>
          <RefreshCw className="h-4 w-4" />
          {retryLabel}
        </Button>
      ) : null}
    </GlassCard>
  );
}

export function PageSkeleton({ testId, cards = 4 }) {
  return (
    <div className="space-y-6" data-testid={testId} aria-busy="true">
      <span className="sr-only">Loading</span>
      <div className="space-y-2">
        <Skeleton className="h-10 w-56 rounded-2xl bg-foreground/10" />
        <Skeleton className="h-4 w-72 max-w-full rounded-full bg-foreground/10" />
      </div>
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        {Array.from({ length: cards }).map((_, index) => (
          <Skeleton key={index} className="h-28 rounded-[1.75rem] bg-foreground/10" />
        ))}
      </div>
      <Skeleton className="h-80 rounded-[1.75rem] bg-foreground/10" />
    </div>
  );
}

export function LoadingRows({ count = 5 }) {
  return (
    <div className="space-y-3" aria-busy="true">
      <span className="sr-only">Loading</span>
      {Array.from({ length: count }).map((_, index) => (
        <Skeleton key={index} className="h-16 rounded-2xl bg-foreground/10" />
      ))}
    </div>
  );
}

export function SearchField({ value, onChange, placeholder, testId }) {
  return (
    <div className="relative min-w-0 flex-1">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
      <Input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="rounded-full bg-background/70 pl-10 pr-10"
        data-testid={testId}
      />
      {value ? (
        <button
          type="button"
          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full text-muted-foreground hover:text-foreground"
          onClick={() => onChange('')}
          aria-label="Clear search"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}

export function Field({ label, htmlFor, hint, children, className }) {
  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

export function DetailRows({ rows }) {
  return (
    <dl>
      {rows.filter(Boolean).map((row) => (
        <div key={row.label} className="flex items-start justify-between gap-4 border-b border-border/70 py-3 last:border-0">
          <dt className="text-sm text-muted-foreground">{row.label}</dt>
          <dd className="max-w-[65%] break-words text-right text-sm font-medium">{row.value ?? '-'}</dd>
        </div>
      ))}
    </dl>
  );
}

export function RequestStatusBadge({ status, className }) {
  const match = REQUEST_STATUSES.find((item) => item.value === status);
  const tone = match?.tone || 'neutral';
  return (
    <span className={cn('inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset', pillTone[tone], className)}>
      {match?.label || humanize(status)}
    </span>
  );
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  destructive = false,
  loading = false,
  onOpenChange,
  onConfirm,
  testId,
}) {
  return (
    <AlertDialog open={open} onOpenChange={(next) => { if (!loading) onOpenChange(next); }}>
      <AlertDialogContent className={cn(glassStrong, 'max-w-md gap-5 p-6')}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full" disabled={loading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className={cn('rounded-full', destructive && 'bg-destructive text-destructive-foreground hover:bg-destructive/90')}
            disabled={loading}
            onClick={(event) => {
              event.preventDefault();
              onConfirm();
            }}
            data-testid={testId}
          >
            {loading ? 'Working...' : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
